import { useCallback } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { TrackingEventCategory, useEmitUserEvent } from "@lookiero/sty-psp-tracking";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Experiments } from "../../../domain/repositories/ABTestClient";
import { Container } from "../shared/types";
import { experimentNameShortener } from "./experimentNameShortener";
import { List, PROJECT, TrackingEventName, ViewItemList, ViewItemListTrackingEvent } from "./tracking";

interface LookItem {
  readonly imageUrl: string;
  readonly purchased?: boolean;
  readonly variantId: string;
  readonly container: Container;
}

interface Looks {
  readonly layout: string;
  readonly lookId: string;
  readonly items: LookItem[];
}

interface TrackViewItemListFunctionArgs {
  readonly experiments: Experiments | undefined;
  readonly list: List.COMPLETE_YOUR_LOOK | List.TRENDING_FOR_YOU | List.CATEGORIES | List.SALES;
  readonly looks: Looks[];
}

interface TrackViewItemListFunction {
  (args: TrackViewItemListFunctionArgs): void;
}

interface UseTrackViewItemListFunctionArgs {
  readonly country: Country;
  readonly segment: Segment;
  readonly userId: string;
  readonly tradename: Tradename;
}

interface UseTrackViewItemListFunction {
  (agrs: UseTrackViewItemListFunctionArgs): TrackViewItemListFunction;
}

const useTrackViewItemList: UseTrackViewItemListFunction = ({ country, segment, tradename, userId }) => {
  const emitUserEvent = useEmitUserEvent<ViewItemListTrackingEvent>();

  const trackViewItemList: TrackViewItemListFunction = useCallback(
    ({ experiments, list, looks }) => {
      const impressions = looks.map((look) => {
        const { items } = look;

        return items.reduce((acc: ViewItemList[], item: LookItem) => {
          if (item.purchased !== undefined && item.purchased === false) {
            return acc;
          }

          return [
            ...acc,
            {
              brand: "(not set)",
              category: "(not set)",
              name: "(not set)",
              price: 0,
              quantity: 1,
              variant: "(not set)",
              index: 1,
              id: item.variantId,
              list,
              look_id: look.lookId,
            },
          ];
        }, []);
      });

      emitUserEvent({
        event: TrackingEventName.VIEW_ITEM_LIST,
        eventCategory: TrackingEventCategory.LOOK_IN,
        store: country,
        section: PROJECT,
        segment,
        tradename,
        userId,
        userUuid: userId,
        experiments: experiments ? JSON.stringify(experimentNameShortener(experiments)) : undefined,
        ecommerce: {
          impressions,
        },
      });
    },
    [country, emitUserEvent, segment, tradename, userId],
  );

  return trackViewItemList;
};

export { useTrackViewItemList };
